import React, { useState } from 'react';
import { formatTime, formatPhone, phoneHref, waHref, setDriverApproved, setDriverBlocked, getDriverChecklist, approveDriverPendingPhotos, rejectDriverPendingPhotos } from '../services/firebase';
import { STATUS_LABEL, STATUS_CLASS } from '../utils/rides';
import ImageViewerModal from './ImageViewerModal';
import { isDuesHeld } from '../utils/duesHold';

export default function Anna360Modal({ driver, metrics = {}, onClose, onEdit, onDelete, onDispatchControl, act, isSuperAdmin = false }) {
  const [activeTab, setActiveTab] = useState(metrics.complaintCount > 0 ? 'complaints' : 'praises');
  const [viewerImage, setViewerImage] = useState(null);

  if (!driver) return null;

  const photosPending = Boolean(driver.photosPendingReview || driver.pendingPhotoUrl || driver.pendingVehiclePhotoUrl);
  const runAct = act
    || (async (uid, fn, msg) => { try { await fn(); if (msg) window.alert(msg); } catch { window.alert('Action failed'); } });

  const dRides = metrics.rides || [];
  const complaintCount = metrics.complaintCount || 0;
  const totalPraises = metrics.totalPraises || 0;
  const check = getDriverChecklist(driver);
  const holdState = String(driver.adminDispatchHold?.state || '');
  const dispatchHeld = holdState === 'after_trip';
  const canSuspend = driver.isApproved && !driver.isBlocked && !dispatchHeld
    && (driver.isOnline || driver.activeRideId);

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="modal-sheet"
        style={{ maxWidth: 840, maxHeight: '90vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Profile Banner */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            {(driver.photoUrl || driver.pendingPhotoUrl || driver.driverPhoto || driver.profilePhoto) ? (
              <div
                style={{ cursor: 'pointer', position: 'relative' }}
                title="Click to view full profile photo"
                onClick={() => setViewerImage({
                  url: driver.photoUrl || driver.pendingPhotoUrl || driver.driverPhoto || driver.profilePhoto,
                  title: `${driver.name || 'Anna'} — Profile Photo`,
                  subtitle: `Vehicle: ${driver.vehicleNumber || 'Auto'} · Phone: ${formatPhone(driver.phone)}`,
                })}
              >
                <img
                  src={driver.photoUrl || driver.pendingPhotoUrl || driver.driverPhoto || driver.profilePhoto}
                  alt=""
                  style={{ width: 64, height: 64, borderRadius: 16, objectFit: 'cover', border: '2px solid #059669', cursor: 'pointer' }}
                />
              </div>
            ) : (
              <div style={{ width: 64, height: 64, borderRadius: 16, background: '#1e293b', display: 'grid', placeItems: 'center', fontSize: 32 }}>🛺</div>
            )}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>{driver.name || 'Anna'}</h2>
                {driver.isApproved && <span className="badge green">Approved Partner</span>}
                {driver.isOnline && <span className="badge blue">Online</span>}
                {holdState === 'after_trip' && <span className="badge orange">Offline after trip</span>}
                {isDuesHeld(driver) && <span className="badge orange">Dues hold</span>}
                {driver.isBlocked && <span className="badge red">Blocked</span>}
              </div>
              <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                📞 {formatPhone(driver.phone)} · Auto: <strong className="mono">{driver.vehicleNumber || '—'}</strong> · Joined: {formatTime(driver.createdAt)}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isSuperAdmin && driver.isApproved && !driver.isBlocked && dispatchHeld && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ color: '#0f766e', borderColor: 'rgba(13, 148, 136, 0.35)' }}
                onClick={() => onDispatchControl?.(driver, 'clear')}
                title="Keep this Anna available after their current trip"
              >
                Cancel scheduled offline
              </button>
            )}
            {isSuperAdmin && canSuspend && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ color: '#dc2626', borderColor: 'rgba(220, 38, 38, 0.35)' }}
                onClick={() => onDispatchControl?.(driver, 'suspend')}
              >
                {driver.activeRideId ? '⏳ Stop after trip' : '🔴 Take offline'}
              </button>
            )}
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* KPI Scorecard Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
          <div style={{ background: '#0f172a', padding: 12, borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>⭐ Rating Score</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#fbbf24', marginTop: 4 }}>
              {driver.ratingCount > 0 && driver.ratingAvg ? `${Number(driver.ratingAvg).toFixed(1)}★` : 'New'}
            </div>
            <div className="muted" style={{ fontSize: 11 }}>{driver.ratingCount || 0} reviews</div>
          </div>

          <div style={{ background: '#0f172a', padding: 12, borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>🛺 Lifetime Rides</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#34d399', marginTop: 4 }}>
              {metrics.totalCompletedTrips ?? Math.max(Number(driver.totalCompletedRides) || 0, Number(driver.lifetimeRides) || 0, Number(driver.completedRides) || 0, 0)}
            </div>
            <div className="muted" style={{ fontSize: 11 }}>{metrics.cancelledRides?.length || 0} cancelled</div>
          </div>

          <div style={{ background: '#0f172a', padding: 12, borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>💵 Total Cash Fare</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#38bdf8', marginTop: 4 }}>
              ₹{metrics.totalCash ?? Number(driver.totalFareEarnings) ?? 0}
            </div>
            <div className="muted" style={{ fontSize: 11 }}>Generated for Anna</div>
          </div>

          <div style={{ background: complaintCount > 0 ? 'rgba(239, 68, 68, 0.1)' : '#0f172a', padding: 12, borderRadius: 12, border: complaintCount > 0 ? '1px solid #ef4444' : '1px solid rgba(255,255,255,0.06)' }}>
            <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: complaintCount > 0 ? '#f87171' : undefined }}>🚩 Complaints</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: complaintCount > 0 ? '#f87171' : '#94a3b8', marginTop: 4 }}>
              {complaintCount}
            </div>
            <div className="muted" style={{ fontSize: 11 }}>Passenger flags</div>
          </div>

          <div style={{ background: metrics.riskLevel === 'high' ? 'rgba(239, 68, 68, 0.15)' : metrics.riskLevel === 'moderate' ? 'rgba(245, 158, 11, 0.15)' : '#0f172a', padding: 12, borderRadius: 12, border: metrics.riskLevel === 'high' ? '1px solid #ef4444' : metrics.riskLevel === 'moderate' ? '1px solid #f59e0b' : '1px solid rgba(255,255,255,0.06)' }}>
            <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: metrics.riskLevel === 'high' ? '#f87171' : metrics.riskLevel === 'moderate' ? '#fbbf24' : '#10b981' }}>
              🛡️ Risk Score
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: metrics.riskLevel === 'high' ? '#f87171' : metrics.riskLevel === 'moderate' ? '#fbbf24' : '#10b981', marginTop: 4 }}>
              {metrics.riskScore ?? 100}/100
            </div>
            <div className="muted" style={{ fontSize: 11 }}>
              {metrics.riskLevel === 'high' ? '🔴 High Risk' : metrics.riskLevel === 'moderate' ? '🟡 Moderate' : '🟢 Safe Partner'}
            </div>
          </div>
        </div>

        {/* Risk Alerts Banner */}
        {metrics.riskFactors?.length > 0 && (
          <div style={{ background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
            <strong style={{ color: '#f87171', fontSize: 13 }}>⚠️ Operational Risk Alerts Detected:</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: '#fca5a5', fontSize: 12 }}>
              {metrics.riskFactors.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Dossier Navigation Tabs */}
        <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 8, marginBottom: 16 }}>
          <button
            type="button"
            className={`btn btn-sm ${activeTab === 'complaints' ? 'btn-primary' : 'btn-ghost'}`}
            style={activeTab === 'complaints' && complaintCount > 0 ? { background: '#dc2626', borderColor: '#dc2626' } : undefined}
            onClick={() => setActiveTab('complaints')}
          >
            🚩 Disciplinary &amp; Complaints ({complaintCount})
          </button>
          <button
            type="button"
            className={`btn btn-sm ${activeTab === 'praises' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setActiveTab('praises')}
          >
            🌟 Praises &amp; Compliments ({totalPraises})
          </button>
          <button
            type="button"
            className={`btn btn-sm ${activeTab === 'rides' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setActiveTab('rides')}
          >
            🛺 Recent Rides ({dRides.length})
          </button>
          <button
            type="button"
            className={`btn btn-sm ${activeTab === 'docs' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setActiveTab('docs')}
          >
            📄 Onboarding &amp; Docs
          </button>
        </div>

        {/* TAB 1: DISCIPLINARY & COMPLAINTS */}
        {activeTab === 'complaints' && (
          <div>
            {complaintCount === 0 ? (
              <div style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.2)', padding: 24, borderRadius: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 32 }}>🛡️</div>
                <div style={{ fontWeight: 800, color: '#34d399', fontSize: 16, marginTop: 6 }}>Clean Disciplinary Track Record</div>
                <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>This Anna has zero reported incidents of extra cash, wrong fares, bad routes, or vehicle mismatches.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* Extra Money Incidents */}
                {(metrics.extraMoneyIncidents || []).map((e) => (
                  <div key={e.id} style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', padding: 12, borderRadius: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span className="badge red" style={{ fontWeight: 800 }}>🚨 Extra Money Demanded</span>
                      <span className="muted mono" style={{ fontSize: 11 }}>{formatTime(e.at)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#e2e8f0', marginTop: 6 }}>
                      Passenger reported that Anna demanded additional cash over the meter/app fare (Fare: ₹{e.fare || '—'}).
                    </div>
                    <div className="muted mono" style={{ fontSize: 11, marginTop: 4 }}>
                      Ride ID: {e.rideId}
                    </div>
                  </div>
                ))}

                {/* Incorrect Fare Reports */}
                {(metrics.wrongFareIncidents || []).map((e) => (
                  <div key={e.id} style={{ background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)', padding: 12, borderRadius: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span className="badge red">💰 Incorrect Fare Reported</span>
                      <span className="muted mono" style={{ fontSize: 11 }}>{formatTime(e.at)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#e2e8f0', marginTop: 6 }}>
                      Passenger flagged that the fare charged did not match the agreed price (Fare: ₹{e.fare || '—'}).
                    </div>
                    <div className="muted mono" style={{ fontSize: 11, marginTop: 4 }}>
                      Ride ID: {e.rideId}
                    </div>
                  </div>
                ))}

                {/* Plate Mismatches */}
                {(metrics.plateMismatchIncidents || []).map((r) => (
                  <div key={r.id} style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.3)', padding: 12, borderRadius: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span className="badge orange">⚠️ Auto Plate Mismatch</span>
                      <span className="muted mono" style={{ fontSize: 11 }}>{formatTime(r.vehicleMatchAt || r.createdAt)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#e2e8f0', marginTop: 6 }}>
                      Passenger reported arriving auto plate did not match registered auto <strong>{r.vehicleNumber}</strong>.
                    </div>
                    <div className="muted mono" style={{ fontSize: 11, marginTop: 4 }}>
                      Ride ID: {r.id} · Passenger: {r.customerName} ({r.customerPhone})
                    </div>
                  </div>
                ))}

                {/* Bad Route Detours */}
                {(metrics.badRouteIncidents || []).map((e) => (
                  <div key={e.id} style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)', padding: 12, borderRadius: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span className="badge orange">🗺️ Route / Detour Issue</span>
                      <span className="muted mono" style={{ fontSize: 11 }}>{formatTime(e.at)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#e2e8f0', marginTop: 6 }}>
                      Passenger reported driver took an incorrect or circuitous route.
                    </div>
                    <div className="muted mono" style={{ fontSize: 11, marginTop: 4 }}>
                      Ride ID: {e.rideId}
                    </div>
                  </div>
                ))}

                {/* Low Ratings with comments */}
                {(metrics.lowRatingRides || []).map((r) => (
                  <div key={r.id} style={{ background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.2)', padding: 12, borderRadius: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span className="badge red">😞 Low Rating: {r.rating}★</span>
                      <span className="muted mono" style={{ fontSize: 11 }}>{formatTime(r.ratedAt || r.updatedAt)}</span>
                    </div>
                    {r.ratingComment && (
                      <div style={{ fontSize: 13, color: '#e2e8f0', marginTop: 6, fontStyle: 'italic' }}>
                        "{r.ratingComment}"
                      </div>
                    )}
                    <div className="muted mono" style={{ fontSize: 11, marginTop: 4 }}>
                      Ride ID: {r.id} · Passenger: {r.customerName} ({r.customerPhone}) · Tags: [{(r.ratingTags || []).join(', ') || 'none'}]
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB 2: PRAISES & COMPLIMENTS */}
        {activeTab === 'praises' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
              <div style={{ background: '#0f172a', padding: 12, borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
                <div style={{ fontSize: 24 }}>😊</div>
                <div style={{ fontWeight: 800, fontSize: 18, color: '#34d399', marginTop: 4 }}>{metrics.praises?.polite || 0}</div>
                <div className="muted" style={{ fontSize: 12 }}>Polite Anna Badges</div>
              </div>

              <div style={{ background: '#0f172a', padding: 12, borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
                <div style={{ fontSize: 24 }}>✨</div>
                <div style={{ fontWeight: 800, fontSize: 18, color: '#38bdf8', marginTop: 4 }}>{metrics.praises?.clean || 0}</div>
                <div className="muted" style={{ fontSize: 12 }}>Clean Auto Badges</div>
              </div>

              <div style={{ background: '#0f172a', padding: 12, borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
                <div style={{ fontSize: 24 }}>🛡️</div>
                <div style={{ fontWeight: 800, fontSize: 18, color: '#fbbf24', marginTop: 4 }}>{metrics.praises?.safeDriving || 0}</div>
                <div className="muted" style={{ fontSize: 12 }}>Safe Driving Badges</div>
              </div>
            </div>

            <div className="card-title" style={{ fontSize: 14, marginBottom: 10 }}>Passenger Reviews &amp; Feedback</div>
            {dRides.filter((r) => r.ratingComment || (r.rating && r.rating >= 4)).length === 0 ? (
              <div className="muted" style={{ fontSize: 13 }}>No passenger text reviews yet for this Anna.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {dRides.filter((r) => r.ratingComment || (r.rating && r.rating >= 4)).slice(0, 10).map((r) => (
                  <div key={r.id} style={{ background: '#0f172a', padding: 12, borderRadius: 10, border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#fbbf24', fontWeight: 800 }}>{r.rating || 5} ★</span>
                      <span className="muted mono" style={{ fontSize: 11 }}>{formatTime(r.ratedAt || r.updatedAt)}</span>
                    </div>
                    {r.ratingComment && (
                      <div style={{ fontSize: 13, color: '#e2e8f0', marginTop: 4 }}>
                        "{r.ratingComment}"
                      </div>
                    )}
                    {r.ratingTags?.length > 0 && (
                      <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                        {r.ratingTags.map((tag) => (
                          <span key={tag} className="badge green" style={{ fontSize: 10 }}>{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB 3: RIDES HISTORY */}
        {activeTab === 'rides' && (
          <div>
            {dRides.length === 0 ? (
              <div className="muted" style={{ fontSize: 13 }}>No rides in database for this Anna.</div>
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>DATE</th>
                      <th>ROUTE</th>
                      <th>FARE</th>
                      <th>PASSENGER</th>
                      <th>STATUS</th>
                      <th>RATING</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dRides.slice(0, 20).map((r) => (
                      <tr key={r.id}>
                        <td className="mono" style={{ fontSize: 11 }}>{formatTime(r.createdAt)}</td>
                        <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.pickup?.address || '—'} → {r.destination?.name || '—'}
                        </td>
                        <td style={{ fontWeight: 700 }}>₹{r.fare}</td>
                        <td>
                          <div>{r.customerName || 'Passenger'}</div>
                          <div className="muted mono" style={{ fontSize: 10 }}>{r.customerPhone}</div>
                        </td>
                        <td>
                          <span className={`badge ${STATUS_CLASS[r.status] || 'gray'}`}>{STATUS_LABEL[r.status] || r.status}</span>
                        </td>
                        <td>{r.rating ? `${r.rating}★` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 4: DOCUMENTS & VEHICLE */}
        {activeTab === 'docs' && (
          <div>
            {photosPending && (
              <div style={{
                border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.08)',
                borderRadius: 10, padding: 14, marginBottom: 16,
              }}>
                <div style={{ fontWeight: 800, color: '#f59e0b', marginBottom: 10 }}>
                  📷 New photo waiting for your review
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {driver.pendingPhotoUrl && (
                    <div>
                      <label className="field-label" style={{ margin: '0 0 4px' }}>NEW DRIVER PHOTO</label>
                      <img
                        src={driver.pendingPhotoUrl}
                        alt="Pending driver photo"
                        onClick={() => setViewerImage({ url: driver.pendingPhotoUrl, title: `New photo — ${driver.name || 'Anna'}` })}
                        style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer', display: 'block' }}
                      />
                      {driver.photoUrl && <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>Replaces the current live photo</div>}
                    </div>
                  )}
                  {driver.pendingVehiclePhotoUrl && (
                    <div>
                      <label className="field-label" style={{ margin: '0 0 4px' }}>NEW AUTO PHOTO</label>
                      <img
                        src={driver.pendingVehiclePhotoUrl}
                        alt="Pending auto photo"
                        onClick={() => setViewerImage({ url: driver.pendingVehiclePhotoUrl, title: `New auto photo — ${driver.vehicleNumber || 'Auto'}` })}
                        style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer', display: 'block' }}
                      />
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => runAct(driver.id, () => approveDriverPendingPhotos(driver.id), 'New photo approved — now live')}
                  >
                    ✓ Approve new photo
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      if (window.confirm('Discard this new photo and keep the current one?')) {
                        runAct(driver.id, () => rejectDriverPendingPhotos(driver.id), 'New photo discarded');
                      }
                    }}
                  >
                    ✕ Reject
                  </button>
                </div>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <label className="field-label" style={{ margin: 0 }}>ANNA DRIVER PHOTO</label>
                  {(driver.photoUrl || driver.pendingPhotoUrl || driver.driverPhoto || driver.profilePhoto) && (
                    <span className="muted" style={{ fontSize: 11 }}>🔍 Click to view</span>
                  )}
                </div>
                {(driver.photoUrl || driver.pendingPhotoUrl || driver.driverPhoto || driver.profilePhoto) ? (
                  <div
                    style={{ position: 'relative', cursor: 'pointer', overflow: 'hidden', borderRadius: 8 }}
                    onClick={() => setViewerImage({
                      url: driver.photoUrl || driver.pendingPhotoUrl || driver.driverPhoto || driver.profilePhoto,
                      title: `Anna Driver Photo — ${driver.name || 'Anna'}`,
                      subtitle: `Vehicle: ${driver.vehicleNumber || 'Auto'} · Phone: ${formatPhone(driver.phone)}`,
                    })}
                  >
                    <img
                      src={driver.photoUrl || driver.pendingPhotoUrl || driver.driverPhoto || driver.profilePhoto}
                      alt="Driver Photo"
                      style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', display: 'block', cursor: 'pointer' }}
                    />
                    <div style={{
                      position: 'absolute', bottom: 6, right: 6, background: 'rgba(0,0,0,0.8)',
                      color: '#fff', fontSize: 11, padding: '3px 8px', borderRadius: 6, fontWeight: 600,
                      backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.15)',
                    }}>
                      🔍 View Full Image
                    </div>
                  </div>
                ) : (
                  <div style={{ height: 160, background: 'var(--surface)', borderRadius: 8, display: 'grid', placeItems: 'center', color: 'var(--muted)' }}>No Photo Uploaded</div>
                )}
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <label className="field-label" style={{ margin: 0 }}>AUTO VEHICLE PHOTO</label>
                  {(driver.vehiclePhotoUrl || driver.pendingVehiclePhotoUrl || driver.rcPhotoUrl || driver.vehiclePhoto) && (
                    <span className="muted" style={{ fontSize: 11 }}>🔍 Click to view</span>
                  )}
                </div>
                {(driver.vehiclePhotoUrl || driver.pendingVehiclePhotoUrl || driver.rcPhotoUrl || driver.vehiclePhoto) ? (
                  <div
                    style={{ position: 'relative', cursor: 'pointer', overflow: 'hidden', borderRadius: 8 }}
                    onClick={() => setViewerImage({
                      url: driver.vehiclePhotoUrl || driver.pendingVehiclePhotoUrl || driver.rcPhotoUrl || driver.vehiclePhoto,
                      title: `Auto Vehicle Photo — ${driver.vehicleNumber || 'Auto'}`,
                      subtitle: `Model: ${driver.vehicleModel || 'Bajaj RE Auto'} · Driver: ${driver.name || 'Anna'}`,
                    })}
                  >
                    <img
                      src={driver.vehiclePhotoUrl || driver.pendingVehiclePhotoUrl || driver.rcPhotoUrl || driver.vehiclePhoto}
                      alt="Vehicle Photo"
                      style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', display: 'block', cursor: 'pointer' }}
                    />
                    <div style={{
                      position: 'absolute', bottom: 6, right: 6, background: 'rgba(0,0,0,0.8)',
                      color: '#fff', fontSize: 11, padding: '3px 8px', borderRadius: 6, fontWeight: 600,
                      backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.15)',
                    }}>
                      🔍 View Full Image
                    </div>
                  </div>
                ) : (
                  <div style={{ height: 160, background: 'var(--surface)', borderRadius: 8, display: 'grid', placeItems: 'center', color: 'var(--muted)' }}>No Vehicle Photo</div>
                )}
              </div>
            </div>

            <div style={{ background: '#0f172a', padding: 14, borderRadius: 10 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Registration Details</div>
              <div style={{ fontSize: 13 }}>Vehicle Plate: <strong className="mono">{driver.vehicleNumber || 'Unassigned'}</strong></div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Vehicle Model: <strong>{driver.vehicleModel || 'Bajaj RE Auto'}</strong></div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Phone: <strong>{formatPhone(driver.phone)}</strong></div>
              {driver.adminNote && (
                <div style={{ fontSize: 13, marginTop: 6, color: '#fbbf24' }}>Admin Note: <strong>{driver.adminNote}</strong></div>
              )}
            </div>
          </div>
        )}

        {/* Action Bar */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16, marginTop: 20 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {phoneHref(driver.phone) && (
              <a className="btn btn-primary btn-sm" href={phoneHref(driver.phone)}>📞 Call Anna</a>
            )}
            {waHref(driver.phone) && (
              <a className="btn btn-ghost btn-sm" href={waHref(driver.phone, `Hi ${driver.name || ''} Anna, MyTownly support team reaching out regarding your account.`)} target="_blank" rel="noreferrer">💬 WhatsApp</a>
            )}
            {waHref(driver.phone) && complaintCount > 0 && (
              <a className="btn btn-orange btn-sm" href={waHref(driver.phone, `Hi ${driver.name || ''} Anna, we received a passenger report regarding extra cash/fare on your recent trip. Please adhere strictly to app fares.`)} target="_blank" rel="noreferrer">⚠️ Send Warning Notice</a>
            )}
            {onDelete && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                onClick={() => onDelete(driver)}
              >
                🗑️ Delete Anna
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            {!driver.isApproved && (
              <button
                type="button"
                className="btn btn-green btn-sm"
                onClick={() => {
                  act(driver.id, () => setDriverApproved(driver.id, true), 'Anna approved');
                  onClose();
                }}
              >
                ✓ Approve
              </button>
            )}
            {driver.isApproved && (
              <button
                type="button"
                className="btn btn-orange btn-sm"
                onClick={() => {
                  act(driver.id, () => setDriverApproved(driver.id, false), 'Anna unapproved');
                  onClose();
                }}
              >
                Unapprove
              </button>
            )}
            {isSuperAdmin && (!driver.isBlocked ? (
              <button
                type="button"
                className="btn btn-red btn-sm"
                onClick={() => {
                  act(driver.id, () => setDriverBlocked(driver.id, true), 'Anna blocked');
                  onClose();
                }}
              >
                🚫 Block Anna
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  act(driver.id, () => setDriverBlocked(driver.id, false), 'Anna unblocked');
                  onClose();
                }}
              >
                Unblock
              </button>
            ))}
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>

      {/* High-Resolution Full Image Viewer Modal */}
      {viewerImage && (
        <ImageViewerModal
          imageUrl={viewerImage.url}
          title={viewerImage.title}
          subtitle={viewerImage.subtitle}
          onClose={() => setViewerImage(null)}
        />
      )}
    </div>
  );
}
